import React, {useState} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {ButtonDropdownField, DropdownList, FlexRow, Modal, Text} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {AssociateClientToProjectModal_viewer} from './__generated__/AssociateClientToProjectModal_viewer.graphql';
import UpdateProjectMutation from '../../../../mutations/update_project_mutation.modern';
import Util from '../../../shared/util/util';
import {MODAL_TYPE, showModal} from '../../../shared/components/modals/generic_modal_conductor';

type AssociateClientToProjectModalProps = {
	projectId: string;
	closeModal: () => void;
	viewer: AssociateClientToProjectModal_viewer;
};

const AssociateClientToProjectModal = ({projectId, closeModal, viewer}: AssociateClientToProjectModalProps) => {
	const {formatMessage} = useIntl();
	const [clientId, setClientId] = useState<string | undefined>(undefined);
	const [selectedClient, setSelectedClient] = useState(
		viewer.company?.clients?.edges?.find(client => client?.node?.id === clientId)
	);
	const clients = viewer.company?.clients?.edges?.map(edge => edge?.node);

	const onSuccess = () => {
		closeModal();
		showModal({
			type: MODAL_TYPE.ADD_PROJECT_BILL_TO,
			projectId: projectId,
		});
	};

	const associateClientToProject = () => {
		const project = {id: projectId};
		Util.CommitMutation(UpdateProjectMutation, {project: project, clientId: clientId}, onSuccess);
	};

	const handleSelectClient = clientId => {
		setClientId(clientId);
		setSelectedClient(viewer.company?.clients?.edges?.find(client => client?.node?.id === clientId));
	};

	return (
		<Modal onCloseModal={closeModal}>
			<Modal.Title>{formatMessage({id: 'settings_finance.select_client'})}</Modal.Title>
			<Modal.Content>
				<>
					<Text size={'1'}>
						<b>{formatMessage({id: 'settings_finance.select_client'})}</b>
					</Text>
					<DropdownList>
						<DropdownList.Trigger>
							<ButtonDropdownField>
								{selectedClient
									? selectedClient.node?.name
									: formatMessage({id: 'settings_finance.select_client'})}
							</ButtonDropdownField>
						</DropdownList.Trigger>
						<DropdownList.Content offset={5} align={'start'}>
							{clients?.map(client => (
								<DropdownList.Item key={client?.id} onClick={() => handleSelectClient(client?.id)}>
									<FlexRow>{client?.name}</FlexRow>
								</DropdownList.Item>
							))}
						</DropdownList.Content>
					</DropdownList>
				</>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={associateClientToProject}>
					{formatMessage({id: 'common.associate'})}
				</Modal.PrimaryFooterButton>

				<Modal.SecondaryFooterButton onPress={closeModal}>
					{formatMessage({id: 'common.close'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export const AssociateClientToProjectModalQuery = graphql`
	query AssociateClientToProjectModalQuery {
		viewer {
			actualPersonId
			component(name: "AssociateClientToProjectModal")
			...AssociateClientToProjectModal_viewer
		}
	}
`;

export default createFragmentContainer(AssociateClientToProjectModal, {
	viewer: graphql`
		fragment AssociateClientToProjectModal_viewer on Viewer {
			id
			company {
				id
				clients(first: 100000) {
					edges {
						node {
							id
							name
						}
					}
				}
			}
		}
	`,
});
